import { useEffect, useRef } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'
import { getNavigationOrigin, isBackNavigation, setNavigationOrigin } from './navigationOrigin'

/**
 * Records the page we come from on every navigation. Mounted once, inside the router.
 *
 * Rules, in order:
 * - same pathname: a filter change, not a navigation — nothing to record;
 * - POP: the browser history already knows where to go back to;
 * - back button: it must consume the origin, never create one, otherwise A → B → A would make B the
 *   origin of A and the two pages would send the user back and forth;
 * - REPLACE: the previous page no longer exists in the history (patient creation redirecting to the
 *   created patient, for instance), so we inherit its own origin rather than pointing at it.
 */
export const useNavigationOriginTracker = (): void => {
  const location = useLocation()
  const navigationType = useNavigationType()
  const previous = useRef<{ pathname: string; url: string } | null>(null)

  useEffect(() => {
    const current = {
      pathname: location.pathname,
      url: `${location.pathname}${location.search}${location.hash}`,
    }
    const from = previous.current
    previous.current = current

    if (!from || from.pathname === current.pathname) return
    if (navigationType === 'POP') return

    if (isBackNavigation(location.state)) {
      setNavigationOrigin(from.pathname, null)
      return
    }

    if (navigationType === 'REPLACE') {
      setNavigationOrigin(current.pathname, getNavigationOrigin(from.pathname))
      setNavigationOrigin(from.pathname, null)
      return
    }

    setNavigationOrigin(current.pathname, from.url)
  }, [location, navigationType])
}
